import { useEffect, useRef } from 'react';
import { labelToMin, minToLabel, type GridInterval } from './gridMath.js';
import { useI18n } from '../../i18n/index.js';

/** Precise-entry popover for the selected block (doc 10 §4.3): time inputs, an
 * optional "copy to" day picker, and delete. Times are `type="time"` on the
 * 15-minute step — the keyboard path for people who don't want to drag. */
export function BlockPopover({
  interval,
  copyTargets,
  onChange,
  onCopyTo,
  onDelete,
  onClose,
}: {
  interval: GridInterval;
  copyTargets?: { id: string; label: string }[];
  onChange(next: GridInterval): void;
  onCopyTo?(targetId: string): void;
  onDelete(): void;
  onClose(): void;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="timegrid-popover" ref={ref} role="dialog" aria-label={t('grid.editHours')}>
      <div className="timegrid-popover-times">
        <input
          type="time"
          step={900}
          value={minToLabel(interval.startMin)}
          onChange={(e) => onChange({ ...interval, startMin: labelToMin(e.target.value) })}
          aria-label={t('grid.startTime')}
        />
        <span>–</span>
        <input
          type="time"
          step={900}
          value={minToLabel(interval.endMin)}
          onChange={(e) => onChange({ ...interval, endMin: labelToMin(e.target.value) })}
          aria-label={t('grid.endTime')}
        />
      </div>

      {copyTargets && copyTargets.length > 0 && onCopyTo && (
        <div className="timegrid-popover-copy">
          <span className="field-hint">{t('grid.copyTo')}</span>
          <div className="timegrid-popover-copy-days">
            {copyTargets.map((t) => (
              <button key={t.id} type="button" className="btn btn-ghost btn-xs" onClick={() => onCopyTo(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="timegrid-popover-actions">
        <button type="button" className="btn btn-danger btn-xs" onClick={onDelete}>
          Delete
        </button>
        <button type="button" className="btn btn-primary btn-xs" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
